import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCourseNavStore } from '../../store/courseNavStore';
import { getCourseManifest, itemHref } from '../../services/courseManifest';
import type { CourseItem, CourseItemType, CourseManifest } from '../../types/course';
import styles from './CourseOutlineDrawer.module.css';

/**
 * CourseOutlineDrawer — the Learning Shell's left rail (docs/ide-ux-spec.md
 * §16.1). The "where am I going" counterpart to the right-side Companion dock:
 * a collapsible drawer, present on every authenticated screen, that shows the
 * current course's ordered outline and lets the student jump between items.
 *
 * It only appears when there's a course context (courseNavStore.courseSlug);
 * on the dashboard etc. it renders nothing. Mirrors CompanionDock's a11y model
 * (button + aria-expanded/controls, focus into panel on open, return on
 * Escape/Close, `inert` while closed, reduced-motion + RTL handled).
 *
 * Scope of this slice: the left rail only. Unifying the lab IDE's center into
 * the morphing Learning Shell surface is the next slice.
 */

const TYPE_GLYPH: Record<CourseItemType, string> = {
  lecture: '🎬',
  reading: '📖',
  lab: '🧪',
  assessment: '✅',
};

const CourseOutlineDrawer: React.FC = () => {
  const { t } = useTranslation();
  const courseSlug = useCourseNavStore((s) => s.courseSlug);
  const activeItemId = useCourseNavStore((s) => s.activeItemId);
  const isOpen = useCourseNavStore((s) => s.isOpen);
  const toggle = useCourseNavStore((s) => s.toggle);
  const close = useCourseNavStore((s) => s.close);

  const launcherRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const panelId = useId();

  const [manifest, setManifest] = useState<CourseManifest | null>(null);

  // See CompanionDock: toggle `inert` imperatively to pull the closed panel out
  // of the tab order + a11y tree, and move focus into the panel on open.
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    if (isOpen) {
      el.removeAttribute('inert');
      closeRef.current?.focus();
    } else {
      el.setAttribute('inert', '');
    }
  }, [isOpen]);

  // Load the manifest lazily once the drawer is opened for a course.
  useEffect(() => {
    if (!isOpen || !courseSlug) return;
    let active = true;
    getCourseManifest(courseSlug).then((m) => {
      if (active) setManifest(m);
    });
    return () => {
      active = false;
    };
  }, [isOpen, courseSlug]);

  const handleClose = useCallback(() => {
    close();
    launcherRef.current?.focus();
  }, [close]);

  const handlePanelKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleClose();
      }
    },
    [handleClose],
  );

  // No course context → no outline to show.
  if (!courseSlug) return null;

  // Group items by module, preserving manifest order.
  const groups: { module: string; items: CourseItem[] }[] = [];
  for (const item of manifest?.items ?? []) {
    const key = item.module ?? '';
    let group = groups.find((g) => g.module === key);
    if (!group) {
      group = { module: key, items: [] };
      groups.push(group);
    }
    group.items.push(item);
  }

  return (
    <>
      <button
        ref={launcherRef}
        type="button"
        className={styles.launcher}
        aria-expanded={isOpen}
        aria-controls={panelId}
        aria-label={t('outline.open_label')}
        onClick={toggle}
        hidden={isOpen}
      >
        <span aria-hidden="true" className={styles.launcherGlyph}>
          ☰
        </span>
        <span className={styles.launcherText}>{t('outline.name')}</span>
      </button>

      <aside
        ref={panelRef}
        id={panelId}
        className={`${styles.panel} ${isOpen ? styles.panelOpen : ''}`}
        aria-label={t('outline.name')}
        onKeyDown={handlePanelKeyDown}
      >
        <header className={styles.panelHeader}>
          <span className={styles.panelTitle}>{manifest?.title ?? t('outline.name')}</span>
          <button
            ref={closeRef}
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
            aria-label={t('common.close')}
          >
            <span aria-hidden="true">✕</span>
          </button>
        </header>

        <nav className={styles.body} aria-label={t('course.outline_label')}>
          {manifest === null && <p className={styles.empty}>{t('common.loading')}</p>}
          {groups.map((group) => (
            <section key={group.module || 'ungrouped'} className={styles.module}>
              {group.module && <h2 className={styles.moduleTitle}>{group.module}</h2>}
              <ol className={styles.itemList}>
                {group.items.map((item) => {
                  const isActive = item.id === activeItemId;
                  const navigable = item.status !== 'locked';
                  const rowInner = (
                    <>
                      <span className={styles.itemGlyph} aria-hidden="true">
                        {TYPE_GLYPH[item.type]}
                      </span>
                      <span className={styles.itemTitle}>{item.title}</span>
                    </>
                  );
                  return (
                    <li key={item.id}>
                      {navigable ? (
                        <Link
                          to={itemHref(courseSlug, item)}
                          className={`${styles.item} ${isActive ? styles.itemActive : ''}`}
                          aria-current={isActive ? 'page' : undefined}
                          onClick={handleClose}
                        >
                          {rowInner}
                        </Link>
                      ) : (
                        <span
                          className={`${styles.item} ${styles.itemLocked}`}
                          aria-disabled="true"
                          title={t('course.locked_note')}
                        >
                          {rowInner}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ol>
            </section>
          ))}
        </nav>
      </aside>
    </>
  );
};

export default CourseOutlineDrawer;
